/**
 * THE FLAIRE — Luxury Review Transformer
 * Restructures Judge.me widget HTML into a bespoke luxury layout.
 * Images-first, black & gold, conversion-optimised.
 *
 * Zero API tokens. Works with Judge.me's existing data pipeline.
 * Survives page navigation in SPAs via MutationObserver.
 */

(function () {
  'use strict';

  /* ─── Config ──────────────────────────────────────────────────────── */
  var WIDGET_SELECTOR = '#judgeme_product_reviews, .jdgm-widget';
  var RETRY_DELAY     = 400; // ms between attempts to find the widget
  var MAX_RETRIES     = 30;  // give up after ~12 s

  /* ─── State ───────────────────────────────────────────────────────── */
  var retries        = 0;
  var listObserver   = null;

  /* ─── Boot ────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function boot() {
    waitForWidget();
  }

  /* ─── Wait for widget container ────────────────────────────────────── */
  function waitForWidget() {
    var widget = document.querySelector(WIDGET_SELECTOR);
    if (!widget) {
      if (++retries < MAX_RETRIES) setTimeout(waitForWidget, RETRY_DELAY);
      return;
    }
    attachWidgetObserver(widget);
    if (widget.querySelector('.jdgm-rev')) {
      transformWidget(widget);
    }
  }

  /* ─── Observe widget for initial render ────────────────────────────── */
  function attachWidgetObserver(widget) {
    var obs = new MutationObserver(function () {
      if (!widget.dataset.flaireReady && widget.querySelector('.jdgm-rev')) {
        widget.dataset.flaireReady = '1';
        transformWidget(widget);
        attachListObserver(widget);
      }
    });
    obs.observe(widget, { childList: true, subtree: true });
  }

  /* ─── Observe review list for "load more" pagination ───────────────── */
  function attachListObserver(widget) {
    if (listObserver) return;
    var list = widget.querySelector('.jdgm-revs');
    if (!list) return;
    listObserver = new MutationObserver(function () {
      var fresh = list.querySelectorAll('.jdgm-rev:not([data-flaire])');
      if (fresh.length) {
        processCards(fresh);
        sortByMedia(list);
      }
    });
    listObserver.observe(list, { childList: true });
  }

  /* ─── Main transform ────────────────────────────────────────────────── */
  function transformWidget(widget) {
    buildSummaryGroup(widget);
    extractSummaryCount(widget);
    elevateMediaGallery(widget);
    var list = widget.querySelector('.jdgm-revs');
    if (list) {
      processCards(list.querySelectorAll('.jdgm-rev:not([data-flaire])'));
      sortByMedia(list);
    }
  }

  /* ─── Group score + stars + rating label into a left column ─────────── */
  function buildSummaryGroup(widget) {
    var summary = widget.querySelector('.jdgm-summary');
    if (!summary || summary.querySelector('.flaire-summary__left')) return;

    var score   = summary.querySelector('.jdgm-summary__score');
    var stars   = summary.querySelector('.jdgm-summary__stars');
    var rating  = summary.querySelector('.jdgm-summary__rating');

    if (!score) return;

    var group = el('div', 'flaire-summary__left');
    // Clone each node into the group, then remove the original
    [score, stars, rating].forEach(function (node) {
      if (!node) return;
      group.appendChild(node.cloneNode(true));
      node.parentNode.removeChild(node);
    });

    summary.insertBefore(group, summary.firstElementChild);
  }

  /* ─── Pull the review count below the summary box ──────────────────── */
  function extractSummaryCount(widget) {
    var summary = widget.querySelector('.jdgm-summary');
    if (!summary || widget.querySelector('.flaire-summary__count')) return;

    var countNode = summary.querySelector('.jdgm-summary__count');
    if (!countNode) return;

    var countDiv = el('div', 'flaire-summary__count');
    countDiv.textContent = countNode.textContent.trim().toUpperCase();

    // Insert directly after the summary box
    summary.parentNode.insertBefore(countDiv, summary.nextSibling);
  }

  /* ─── Move customer media gallery above the reviews list ───────────── */
  function elevateMediaGallery(widget) {
    var gallery = widget.querySelector('.jdgm-media-gallery');
    var list    = widget.querySelector('.jdgm-revs');
    if (gallery && list && gallery.querySelector('img')) {
      list.parentNode.insertBefore(gallery, list);
    }
  }

  /* ─── Process individual review cards ──────────────────────────────── */
  function processCards(cards) {
    Array.prototype.forEach.call(cards, function (rev) {
      rev.dataset.flaire = '1';
      hoistPhotos(rev);
      buildMetaBar(rev);
    });
  }

  /* ─── Move review images to top of card, before the title ──────────── */
  function hoistPhotos(rev) {
    var pics  = rev.querySelector('.jdgm-rev__pics');
    var title = rev.querySelector('.jdgm-rev__title');
    if (!pics || !title || !pics.querySelector('img')) return;
    title.parentNode.insertBefore(pics, title);
    rev.classList.add('jdgm-rev--media-first');
  }

  /* ─── Build a clean meta bar (stars | author | badge ·· date) ──────── */
  function buildMetaBar(rev) {
    if (rev.querySelector('.flaire-rev__meta')) return;

    var rating    = rev.querySelector('.jdgm-rev__rating');
    var author    = rev.querySelector('.jdgm-rev__author');
    var badge     = rev.querySelector('.jdgm-verified-badge, [class*="jdgm-badge"]');
    var timestamp = rev.querySelector('.jdgm-rev__timestamp');

    var meta  = el('div', 'flaire-rev__meta');
    var left  = el('div', 'flaire-rev__meta-left');
    var right = el('div', 'flaire-rev__meta-right');

    if (rating)    left.appendChild(rating.cloneNode(true));
    if (author)    left.appendChild(author.cloneNode(true));
    if (badge)     left.appendChild(badge.cloneNode(true));
    if (timestamp) right.appendChild(timestamp.cloneNode(true));

    meta.appendChild(left);
    meta.appendChild(right);

    rev.insertBefore(meta, rev.firstElementChild);

    [rating, author, badge, timestamp].forEach(function (node) {
      if (node && node.parentNode === rev) node.parentNode.removeChild(node);
    });
  }

  /* ─── Sort review list: media-first, then text-only ────────────────── */
  function sortByMedia(list) {
    var cards = Array.prototype.slice.call(list.querySelectorAll('.jdgm-rev[data-flaire]'));
    var withMedia    = cards.filter(function (r) { return !!r.querySelector('.jdgm-rev__pics img'); });
    var withoutMedia = cards.filter(function (r) { return !r.querySelector('.jdgm-rev__pics img'); });
    withMedia.concat(withoutMedia).forEach(function (r) { list.appendChild(r); });
  }

  /* ─── Tiny helper: create element with class + optional text ────────── */
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text)      node.textContent = text;
    return node;
  }

})();
